import { useEffect, useRef } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ACCENT, BORDER, FONT_MONO, SURFACE } from '../../tokens';

interface Logo {
  name: string;
  src: string;
}

const LOGOS: readonly Logo[] = [
  { name: 'Notion', src: '/logos/notion.svg' },
  { name: 'Claude', src: '/logos/claude-icon.png' },
  { name: 'Ollama', src: '/logos/ollama.png' },
  { name: 'Gmail', src: '/logos/gmail.svg' },
  { name: 'Calendar', src: '/logos/calendar.svg' },
  { name: 'n8n', src: '/logos/n8n.svg' },
  { name: 'Slack', src: '/logos/slack.svg' },
  { name: 'Apify', src: '/logos/apify-icon.png' },
];

/** Seconds for one full loop of the track. */
const LOOP_SECONDS_MOBILE = 300;
const LOOP_SECONDS_DESKTOP = 2000;

/**
 * Infinite logo strip.
 *
 * Driven by `requestAnimationFrame` rather than a CSS animation, because the
 * reduced-motion and low-tier stylesheets clamp `animation-duration` globally
 * and would freeze a CSS marquee outright.
 */
export function LogoMarquee() {
  const isMobile = useIsMobile();
  const trackRef = useRef<HTMLDivElement>(null);

  const repeats = isMobile ? 15 : 100;
  const track = Array.from({ length: repeats }, () => LOGOS).flat();

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    let raf = 0;
    let x = 0;

    // The track is rendered twice over, so half its width is one full loop.
    const computeSpeed = (): number => {
      const halfWidth = el.scrollWidth / 2;
      const targetSeconds = isMobile ? LOOP_SECONDS_MOBILE : LOOP_SECONDS_DESKTOP;
      return halfWidth / (targetSeconds * 60); // px per frame at 60fps
    };

    let speed = computeSpeed();

    const step = (): void => {
      x -= speed;
      const halfWidth = el.scrollWidth / 2;
      if (Math.abs(x) >= halfWidth) x = 0;
      el.style.transform = `translateX(${x}px)`;
      raf = requestAnimationFrame(step);
    };

    // Recompute after first paint, when scrollWidth is accurate.
    const timer = setTimeout(() => {
      speed = computeSpeed();
      raf = requestAnimationFrame(step);
    }, 0);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [isMobile]);

  return (
    <div
      style={{
        background: SURFACE,
        borderTop: `1px solid ${BORDER}`,
        borderBottom: `1px solid ${BORDER}`,
        padding: '24px 0 40px',
        overflow: 'hidden',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <span
          style={{
            display: 'inline-block',
            background: ACCENT,
            color: SURFACE,
            padding: '8px 22px',
            borderRadius: 100,
            fontFamily: FONT_MONO,
            fontSize: '0.7rem',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          Built on tools you already own
        </span>
      </div>

      <div ref={trackRef} style={{ display: 'flex', width: 'max-content', willChange: 'transform' }}>
        {track.map((logo, i) => (
          <div
            key={`${logo.name}-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 48px',
              transition: 'transform 0.3s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <img
              src={logo.src}
              alt={logo.name}
              loading="lazy"
              decoding="async"
              style={{
                height: 48,
                width: 'auto',
                maxWidth: 130,
                objectFit: 'contain',
                // These two ship on a white plate; screen blends it away.
                mixBlendMode: logo.name === 'Claude' || logo.name === 'Apify' ? 'screen' : 'normal',
                // Ollama's mark is black-on-transparent, so invert it to read on dark.
                filter: logo.name === 'Ollama' ? 'brightness(0) invert(1)' : 'none',
                opacity: logo.name === 'Ollama' ? 0.9 : 1,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
